import { clsx } from 'clsx'
import { Table } from './Table'
import type { TableCell } from './TableCell'
import type { TableColumn, TableColumnProps } from './TableColumn'
import type { TableRow, TableRowProps } from './TableRow'

interface StyleConfig {
  width?: string
  stickyLeft?: string
}

export class ScrollableTable extends Table {
  private readonly stickyStartColumns?: number
  private readonly baseClassName = 'mdc-data-table'
  private readonly scrollableClassName = `${this.baseClassName}--scrollable`

  constructor(columns: TableColumnProps[], rows: TableRowProps[], stickyStartColumns?: number) {
    super(columns, rows)
    this.validateStickyColumns(stickyStartColumns)
    this.stickyStartColumns = stickyStartColumns
  }

  render(): string {
    return `
      <table class="${this.baseClassName} ${this.scrollableClassName}">
          <thead class="${this.scrollableClassName}__headings">
            <tr>
              ${this.columns.map((col, i) => this.renderHeading(col, i)).join('')}
              ${
                this.hasRowWithLink()
                  ? `<th class="${this.generateClassName(
                      `${this.baseClassName}__heading ${this.scrollableClassName}__heading`,
                      { [`${this.scrollableClassName}__heading--href`]: true }
                    )}" style="width: ${this.getLinkIconSize()}"></th>`
                  : ''
              }
            </tr>
          </thead>
          <tbody>
            ${this.rows.map((row) => this.renderRow(row)).join('')}
          </tbody>
        </table>
    `
  }

  renderHeading(column: TableColumn, index: number): string {
    const isSticky = index < (this.stickyStartColumns ?? 0)
    const allPreviousColumnsWidth = isSticky ? this.calculatePreviousColumnsWidth(index) : 0

    const className = this.generateClassName(
      `${this.baseClassName}__heading ${this.scrollableClassName}__heading`,
      {
        [`${this.baseClassName}__heading--position-${column.align}`]: true,
        [`${this.scrollableClassName}__heading--sticky-start`]: isSticky,
        [`${this.scrollableClassName}__heading--with-width`]: !!column.width,
      }
    )

    const styles = this.generateStyles({
      width: column.width,
      stickyLeft: isSticky ? `${allPreviousColumnsWidth}px` : undefined,
    })

    return `<th class="${className}" ${styles}>${column.label}</th>`
  }

  renderCell(cell: TableCell, column: TableColumn): string {
    const index = this.columns.indexOf(column)
    const isSticky = (this.stickyStartColumns ?? 0) > index
    const allPreviousColumnsWidth = isSticky ? this.calculatePreviousColumnsWidth(index) : 0

    const className = this.generateClassName(`${this.scrollableClassName}__cell`, {
      [`${this.baseClassName}__cell--default-styles`]: cell.applyDefaultStyles,
      [`${this.baseClassName}__cell--position-${cell.align}`]: true,
      [`${this.scrollableClassName}__cell--with-width`]: !!column.width,
      [`${this.scrollableClassName}__cell--sticky-start`]: isSticky,
    })

    const styles = this.generateStyles({
      width: column.width,
      stickyLeft: isSticky ? `${allPreviousColumnsWidth}px` : undefined,
    })

    return `<td class="${className}" ${styles}>${cell.content}</td>`
  }

  renderRow(row: TableRow): string {
    const cellsContent = row.cells
      .map((cell, index) => this.renderCell(cell, this.columns[index]))
      .join('')

    return `
      <tr class="${this.scrollableClassName}__row">
        ${cellsContent}
        ${this.hasRowWithLink() ? this.renderLinkCell(row.href ?? undefined) : ''}
      </tr>
    `
  }

  private validateStickyColumns(stickyStartColumns?: number): void {
    if (!stickyStartColumns || stickyStartColumns <= 0) return

    const hasInvalidStickyColumn = this.columns
      .slice(0, stickyStartColumns)
      .some((column) => !column.width?.endsWith('px'))

    if (hasInvalidStickyColumn) {
      throw new Error('Sticky columns must have a fixed width in pixels.')
    }
  }

  private calculatePreviousColumnsWidth(index: number): number {
    return this.columns.slice(0, index).reduce((acc: number, curr) => {
      const width = curr.width && Number.parseInt(curr.width, 10)
      return width ? acc + width : acc
    }, 0)
  }

  private generateStyles(config: StyleConfig): string {
    const styles: string[] = []

    if (config.width) {
      styles.push(`--_cell-width: ${config.width}`)
    }

    if (config.stickyLeft) {
      styles.push(`--_cell-sticky-left: ${config.stickyLeft}`)
    }

    return styles.length ? `style="${styles.join('; ')}"` : ''
  }

  private generateClassName(baseClass: string, conditions: Record<string, boolean>): string {
    return clsx(
      baseClass,
      Object.entries(conditions).reduce((acc, [className, condition]) => {
        if (condition) acc.push(className)
        return acc
      }, [] as string[])
    )
  }

  private renderLinkCell(href?: string): string {
    const cellLinkClass = this.generateClassName(`${this.scrollableClassName}__cell`, {
      [`${this.baseClassName}__cell--position-right`]: true,
      [`${this.scrollableClassName}__cell--with-width`]: true,
      [`${this.scrollableClassName}__cell--href`]: true,
    })

    const content = href
      ? `<a href="${href}" class="${this.baseClassName}__href-button">
           ${this.renderLinkIcon()}
         </a>`
      : ''

    return `<td class="${cellLinkClass}" style="width: ${this.getLinkIconSize()}">${content}</td>`
  }
}
